import { Outlet } from 'react-router';
import BusinessManagerListener from '~/modules/business-manager/component/bm-listener/BusinessManagerListener';
import { ClientOnly } from 'remix-utils';
import PageLayout from '@modules/professional/component/page-layout/PageLayout';
import type { APHandle, HandleHideNavigation } from '@modules/root/type/handle';
import { useLoaderData } from 'react-router';
import { useProfessionalRevampLayoutActive } from '@modules/professional/hook/use-professional-layout-data';
import { ProfessionalRevampShell } from '~/ui-rocco/pages/professional-detail/ProfessionalRevampShell';
import type { loader } from '@modules/professional/page/loaders/professional-page-preview-loader.server';

const hideNavigation: HandleHideNavigation = {
    desktop: true,
    tablet: true,
    mobile: true,
};

export const handle: APHandle = {
    hideTopNavigation: hideNavigation,
    hideFooter: hideNavigation,
    isBMPreview: true,
    hideContactArchiPro: true,
};

const EditorPreviewRootPage = () => {
    const { primaryCTA } = useLoaderData<typeof loader>();
    const showRevamp = useProfessionalRevampLayoutActive();

    // The BM listener drives tab navigation from the surrounding Business
    // Manager over postMessage, so it mounts on both layouts. Only the revamp
    // reads the live-edit patch, so it is told which era it is in — on the
    // legacy layout it re-fetches instead, rather than acknowledging an
    // override nothing here will read.
    const listener = (
        <ClientOnly fallback={<></>}>
            {() => {
                return <BusinessManagerListener canApplyPatch={showRevamp} />;
            }}
        </ClientOnly>
    );

    // pointerEvents: none makes the preview a picture of the page — Business
    // Manager owns every interaction, so nothing inside the iframe is
    // clickable on either layout.
    return (
        <div style={{ pointerEvents: 'none' }}>
            {showRevamp ? (
                // The revamp owns the whole page, so it replaces PageLayout's
                // legacy banner + tab chrome rather than nesting inside it. No
                // admin controls: the edit toggle and copy-link buttons belong
                // to the public page, not to a preview already inside Business
                // Manager.
                <ProfessionalRevampShell>
                    {listener}
                    <Outlet />
                </ProfessionalRevampShell>
            ) : (
                <PageLayout primaryCTA={primaryCTA}>
                    {listener}
                    <Outlet />
                </PageLayout>
            )}
        </div>
    );
};

export default EditorPreviewRootPage;
